/**
 * The toast host and its hook: one polite live region per workspace, one
 * message at a time, cleared after a delay. The context stays private to this
 * file so `ToastHost` and `useToast` cannot drift apart. Re-exported through
 * `./primitives.js`.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/* ---- Toasts ---------------------------------------------------------- */

interface ToastValue {
  readonly toast: (text: string, ms?: number) => void;
}

const ToastContext = createContext<ToastValue>({ toast: () => undefined });

export function ToastHost({ children }: { readonly children: ReactNode }): ReactNode {
  const [text, setText] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const toast = useCallback((next: string, ms = 4000) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setText(next);
    timer.current = window.setTimeout(() => {
      setText(null);
    }, ms);
  }, []);
  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="mj-ws-toastwrap"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {text !== null ? <div className="mj-ws-toast">{text}</div> : null}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastValue['toast'] {
  return useContext(ToastContext).toast;
}
